import { useCallback, useId, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectStylePanel } from '../../components/panels/ObjectStylePanel';
import { AtomTextPanel } from '../../components/panels/AtomTextPanel';
import { ArrangePanel } from '../../components/panels/ArrangePanel';
import type { BondDrawingConfig } from '@peruselab/structure-editor/domain/bonds';
import { RightPanelStrip } from './RightPanelStrip';
import { EditorToolPalette } from '../../components/panels/EditorToolPalette';
import { EditorToolsSection } from '../../components/panels/EditorToolsSection';
import { SearchFilterRail } from '../../components/panels/SearchFilterRail';
import { MyDrawingsFilter, type MyDrawingsStatus } from '../../components/panels/MyDrawingsFilter';
import { Icon, type IconName } from '../../components/Icon';
import { ComposePanel, type ComposerMode } from './ComposePanel';
import { MarkushSearchSection, type RGroupRevealRequest } from './MarkushSearchSection';
import { ReactionArrowSection } from '../../components/panels/ReactionArrowSection';
import { ReactionAgentRoleSection } from '../../components/panels/ReactionAgentRoleSection';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import type { usePartialCharges } from '../../hooks/usePartialCharges';
import type { useMoleculeSearch } from '../../hooks/useMoleculeSearch';
import type { SearchResultFilters } from '../../hooks/useSearchResultFilters';
import type { StyleDefaultsSyncStatus } from '../../hooks/useStyleDefaultsSync';
import { StructureChecksSection, type StructureChecksActions } from '../../components/panels/StructureChecksSection';
import type { StructureIssue } from '@peruselab/api-client/structureChecks';

export type PanelTab = 'filter' | 'compose' | 'style' | 'text' | 'arrange' | 'discuss';

// #672: a fixed set -- Markush search is a section of FILTER, not a tab that comes and goes.
// #993: Arrange combines editing and arrangement and is the default tab.
// ADR 0016 part C (owner, 2026-10-09): Discuss last -- the open drawing's and its Molecule's threads.
const TAB_ORDER: readonly PanelTab[] = ['arrange', 'filter', 'compose', 'style', 'text', 'discuss'];

const TAB_ICON: Record<PanelTab, IconName> = {
  filter: 'filterList',
  compose: 'dashboardCustomize',
  style: 'palette',
  text: 'stickyNote',
  arrange: 'alignHorizontalLeft',
  discuss: 'forum'
};

interface RightPanelProps {
  readonly doc: UseDrawingDocumentResult;
  readonly partialCharges: ReturnType<typeof usePartialCharges>;
  readonly filters: SearchResultFilters;
  readonly search: ReturnType<typeof useMoleculeSearch>;
  readonly tab: PanelTab;
  readonly markushReveal?: RGroupRevealRequest | undefined;
  readonly onTabChange: (tab: PanelTab) => void;
  readonly bondConfig: BondDrawingConfig;
  readonly onBondColorChange: (color: string) => void;
  readonly onBondNumericChange: (key: keyof BondDrawingConfig, value: string, minimum: number) => void;
  readonly onCompose: (mode: ComposerMode) => void;
  readonly composeTabRef?: RefObject<HTMLButtonElement | null>;
  // #402 — the collapsed tab-strip state (localStorage-backed, viewport-gated in the shell).
  readonly collapsed: boolean;
  readonly setCollapsed: (collapsed: boolean) => void;
  // #575/#576/#577/#578 — the Arrange tab's Tools section (session-only, owned by WorkspaceShell
  // since DrawingPaper also needs these for rendering / pointer-gesture behavior).
  /** ADR 0020 D3 (#1099) -- the server's structure-check issues, listed at the top of Arrange; and
   *  (#1101) what their fixes and "It's intended" do. */
  readonly structureIssues?: readonly StructureIssue[];
  readonly structureCheckActions?: Omit<StructureChecksActions, 'onSelect'>;
  /** #1101 -- the Arrange tab's count of open errors and warnings, coloured by the worst. */
  readonly checksBadge?: { readonly count: number; readonly severity: 'error' | 'warning' } | undefined;
  readonly showNodeNumbers: boolean;
  readonly showStereoLabels: boolean;
  // ADR 0022 (as amended) -- Style's "Show ionic bonds" view option (session-only, like R/S labels).
  readonly showIonicBonds?: boolean;
  readonly onToggleShowIonicBonds?: (value: boolean) => void;
  // ADR 0026 D2 -- Style's "Show hapticity" view option (session-only): η labels on hapto bonds.
  readonly showHapticity?: boolean;
  readonly onToggleShowHapticity?: (value: boolean) => void;
  readonly onToggleShowStereoLabels: (value: boolean) => void;
  readonly onToggleShowNodeNumbers: (value: boolean) => void;
  readonly showBondAcceptors: boolean;
  readonly onToggleShowBondAcceptors: (value: boolean) => void;
  readonly showCenterOfRotation: boolean;
  readonly onToggleShowCenterOfRotation: (value: boolean) => void;
  readonly lassoSelectionEnabled: boolean;
  readonly onToggleLassoSelectionEnabled: (value: boolean) => void;
  /** #862 -- whether the new-object style defaults are saved to the user's account. */
  readonly styleDefaultsStatus?: StyleDefaultsSyncStatus | undefined;
  /** #837 -- set while the My drawings feed fills the results: its Active / Archived filter. */
  readonly myDrawingsFilter?:
    { readonly status: MyDrawingsStatus; readonly onChange: (status: MyDrawingsStatus) => void } | undefined;
  /** ADR 0016 part C -- the Discuss tab's content and its badge (comments on the drawing + Molecule). */
  readonly discussion: ReactNode;
  readonly discussionCount: number | null;
}

// #592 — Style hosts bond defaults; Text hosts supported atom-label controls; Arrange
// exposes alignment, transforms, chemistry controls and 3D rotation.
// ADR 0007 P3/P10 — EDIT originally hosted the atom/bond property editor (`EditorToolPalette`), the
// collapsed `BondSettingsPanel` (P10a-2) and the `BondTaxonomyPanel` reference (P10a-3).
// FILTER hosts the search filter rail (P6). COMPOSE hosts the isomer picker + composer
// launchers (P10b). The tablist follows the WAI-ARIA tabs pattern — roving tabindex, arrow /
// Home / End navigation, one stable tabpanel (P10c). The active tab is owned by
// `WorkspaceShell` (P5) so the document bar's filter toggle can drive it.
export function RightPanel({
  doc,
  partialCharges,
  filters,
  search,
  tab,
  onTabChange,
  markushReveal,
  bondConfig,
  onBondColorChange,
  onBondNumericChange,
  onCompose,
  composeTabRef,
  collapsed,
  setCollapsed,
  structureIssues = [],
  structureCheckActions,
  checksBadge,
  showNodeNumbers,
  showStereoLabels,
  showIonicBonds = false,
  onToggleShowIonicBonds,
  showHapticity = false,
  onToggleShowHapticity,
  onToggleShowStereoLabels,
  onToggleShowNodeNumbers,
  showBondAcceptors,
  onToggleShowBondAcceptors,
  showCenterOfRotation,
  onToggleShowCenterOfRotation,
  lassoSelectionEnabled,
  onToggleLassoSelectionEnabled,
  styleDefaultsStatus,
  myDrawingsFilter,
  discussion,
  discussionCount
}: RightPanelProps) {
  const { t } = useTranslation();
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const tabRefs = useRef<Partial<Record<PanelTab, HTMLButtonElement | null>>>({});

  const expandToTab = useCallback(
    (next: PanelTab) => {
      onTabChange(next);
      setCollapsed(false);
    },
    [onTabChange, setCollapsed]
  );

  const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = TAB_ORDER.indexOf(tab);
    let next: PanelTab | undefined;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = TAB_ORDER[(index + 1) % TAB_ORDER.length];
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
      next = TAB_ORDER[(index - 1 + TAB_ORDER.length) % TAB_ORDER.length];
    else if (event.key === 'Home') next = TAB_ORDER[0];
    else if (event.key === 'End') next = TAB_ORDER[TAB_ORDER.length - 1];
    if (!next || next === tab) return;
    event.preventDefault();
    onTabChange(next);
    tabRefs.current[next]?.focus();
  };

  const tabButton = (value: PanelTab, label: string) => (
    <button
      key={value}
      ref={(el) => {
        tabRefs.current[value] = el;
        if (value === 'compose' && composeTabRef) composeTabRef.current = el;
      }}
      type="button"
      role="tab"
      id={`${baseId}-${value}-tab`}
      aria-selected={tab === value}
      aria-controls={panelId}
      tabIndex={tab === value ? 0 : -1}
      className={tab === value ? 'active' : ''}
      {...(value === 'arrange' && checksBadge
        ? { title: t('structureCheck.badge', { count: checksBadge.count }) }
        : {})}
      onClick={() => onTabChange(value)}
    >
      {value === 'arrange' && checksBadge ? (
        <span className="workspace-tab-icon">
          <Icon name={TAB_ICON[value]} size="1.15rem" />
          {/* Hidden from the tab's name ("Arrange" stays its name); the count is its tooltip instead. */}
          <span className={`workspace-tab-badge workspace-tab-badge--${checksBadge.severity}`} aria-hidden="true">
            {checksBadge.count}
          </span>
        </span>
      ) : value === 'discuss' && discussionCount ? (
        // On the icon's corner, so the count takes no width from the label in a narrow column.
        <span className="workspace-tab-icon">
          <Icon name={TAB_ICON[value]} size="1.15rem" />
          <span className="workspace-tab-badge" aria-label={t('discussions.cardCount', { count: discussionCount })}>
            {discussionCount}
          </span>
        </span>
      ) : (
        <Icon name={TAB_ICON[value]} size="1.15rem" />
      )}
      <span>{label}</span>
    </button>
  );

  if (collapsed) {
    return (
      <aside
        className="workspace-right workspace-right--collapsed"
        data-testid="right-panel"
        aria-label={t('workspace.panel.label')}
      >
        <RightPanelStrip tab={tab} onExpand={() => setCollapsed(false)} onExpandToTab={expandToTab} />
      </aside>
    );
  }

  return (
    <aside className="workspace-right" data-testid="right-panel" aria-label={t('workspace.panel.label')}>
      {/* #676 -- the collapse control is a slim column at the panel's inner (left) edge, beside
          both tab rows, instead of a header row of its own. */}
      <div className="workspace-right-head">
        <button
          type="button"
          className="icon-button workspace-right-toggle"
          aria-label={t('workspace.panel.collapse')}
          title={t('workspace.panel.collapse')}
          onClick={() => setCollapsed(true)}
        >
          <Icon name="chevronRight" />
        </button>
        <div
          className="workspace-right-tabs"
          role="tablist"
          aria-label={t('workspace.panel.label')}
          tabIndex={-1}
          onKeyDown={onTabKeyDown}
        >
          {tabButton('arrange', t('workspace.panel.arrange'))}
          {tabButton('filter', t('workspace.panel.filter'))}
          {tabButton('compose', t('workspace.panel.compose'))}
          {tabButton('style', t('workspace.panel.style'))}
          {tabButton('text', t('workspace.panel.text'))}
          {tabButton('discuss', t('workspace.panel.discuss'))}
        </div>
      </div>
      <div role="tabpanel" id={panelId} aria-labelledby={`${baseId}-${tab}-tab`} className="workspace-right-body">
        {tab === 'arrange' ? (
          <>
            <StructureChecksSection
              issues={structureIssues}
              onSelect={(issue) => doc.setSelection({ atomIds: issue.atomIds, bondIds: issue.bondIds })}
              {...structureCheckActions}
            />
            <ReactionArrowSection doc={doc} />
            <ReactionAgentRoleSection doc={doc} />
            <EditorToolsSection
              showNodeNumbers={showNodeNumbers}
              showStereoLabels={showStereoLabels}
              onToggleShowStereoLabels={onToggleShowStereoLabels}
              showHydrogens={doc.hydrogensShown}
              onToggleShowHydrogens={doc.setHydrogensShown}
              groupsAsLabels={doc.groupsContracted}
              onToggleGroupsAsLabels={(value) => void doc.setGroupsContracted(value)}
              onToggleShowNodeNumbers={onToggleShowNodeNumbers}
              showBondAcceptors={showBondAcceptors}
              onToggleShowBondAcceptors={onToggleShowBondAcceptors}
              showCenterOfRotation={showCenterOfRotation}
              onToggleShowCenterOfRotation={onToggleShowCenterOfRotation}
              lassoSelectionEnabled={lassoSelectionEnabled}
              onToggleLassoSelectionEnabled={onToggleLassoSelectionEnabled}
            />
            <EditorToolPalette doc={doc} partialCharges={partialCharges} />
            <ArrangePanel doc={doc} />
            {/* #840 (owner): Bond taxonomy is out of the Edit tab; its API serves the bond glossary
                (#1126) now. */}
          </>
        ) : tab === 'style' ? (
          <ObjectStylePanel
            doc={doc}
            bondConfig={bondConfig}
            onColorChange={onBondColorChange}
            onNumericChange={onBondNumericChange}
            defaultsStatus={styleDefaultsStatus}
            showIonicBonds={showIonicBonds}
            {...(onToggleShowIonicBonds ? { onToggleShowIonicBonds } : {})}
            showHapticity={showHapticity}
            {...(onToggleShowHapticity ? { onToggleShowHapticity } : {})}
          />
        ) : tab === 'text' ? (
          <AtomTextPanel doc={doc} />
        ) : tab === 'discuss' ? (
          discussion
        ) : tab === 'compose' ? (
          <ComposePanel onCompose={onCompose} />
        ) : (
          // #672/#674: a Markush drawing *is* a Markush query, so its section leads the tab while
          // the drawing has Markush content; a plain drawing has none.
          <>
            {myDrawingsFilter ? (
              <MyDrawingsFilter status={myDrawingsFilter.status} onChange={myDrawingsFilter.onChange} />
            ) : null}
            {doc.hasMarkushContent ? <MarkushSearchSection doc={doc} revealRequest={markushReveal} /> : null}
            <SearchFilterRail
              filters={filters}
              reactionBuckets={search.reactionBuckets}
              onToggleReactionBucket={search.toggleReactionBucket}
              structuralConstraints={search}
            />
          </>
        )}
      </div>
    </aside>
  );
}
